"use client";

import { useState } from "react";
import { Backdrop, Box, Divider, Popover, Typography } from "@mui/material";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { usePageBackdrop } from "./usePageBackdrop";
import {
  BOX_SHADOW_SIZE_MEDIUM,
  BRAND_COLOR,
  HIDE_SCROLLBAR_STYLE,
  META_AUSTRALIAS_NUMBER_1,
  META_BRAND_NAME,
  NAV_SELECTED_BACKGROUND_COLOR,
} from "./LayoutConstants";
import useHasSubNav from "./useHasSubNav";
import PillButton from "../shared/PillButton";
import { useTopNavHeadroom } from "./useTopNavHeadroom";
import Clarity from "@microsoft/clarity";
import { signOut } from "next-auth/react";
import PersonIcon from "@mui/icons-material/Person";
import { AppendReturnUrl } from "@/services/AppendReturnUrl";
import { useLazySession } from "./useLazySession";
import { BuyMenu } from "./BuyMenu";

Clarity.init("rei1n2dyyn");

export const TOP_NAV_WIDTH_MOBILE_MAX = "lg";

const DESKTOP_SPACING = 4;
const MOBILE_SPACING = 2;
export const TOP_NAV_Z_INDEX = 1300;

export const TOP_NAV_HEIGHT_DESKTOP_NUMBER = 70;
export const TOP_NAV_HEIGHT_DESKTOP = TOP_NAV_HEIGHT_DESKTOP_NUMBER + "px";
export const TOP_NAV_HEIGHT_MOBILE_NUMBER = 50;
export const TOP_NAV_HEIGHT_MOBILE = TOP_NAV_HEIGHT_MOBILE_NUMBER + "px";

const TOP_NAV_LOGO_WIDTH_DESKTOP = 270;
const TOP_NAV_LOGO_WIDTH_MOBILE = 216;

function NAV({
  children,
  isMobileMenuOpen,
}: {
  children: React.ReactNode;
  isMobileMenuOpen: boolean;
}) {
  const { hasSubNav } = useHasSubNav();
  const { isCollapsed } = useTopNavHeadroom();
  return (
    <Box
      component="nav"
      sx={(theme) => ({
        zIndex: TOP_NAV_Z_INDEX,
        display: "flex",
        position: "fixed",
        width: "100%",
        top: 0,
        background: "white",
        height: TOP_NAV_HEIGHT_DESKTOP,
        boxShadow: !hasSubNav || isCollapsed ? BOX_SHADOW_SIZE_MEDIUM : "none",
        borderBottom: "1px solid",
        borderColor: "grey.200",
        [theme.breakpoints.down(TOP_NAV_WIDTH_MOBILE_MAX)]: {
          height: isMobileMenuOpen ? "auto" : TOP_NAV_HEIGHT_MOBILE,
        },
        "*": {
          fontWeight: "600 !important",
        },
        ul: {
          listStyle: "none",
          whiteSpace: "nowrap",
        },
        "> ul": {
          display: "grid",
          gridTemplateColumns: `[logo] ${TOP_NAV_LOGO_WIDTH_DESKTOP + 40}px [left] auto [right] min-content`,
          gridTemplateRows: "[top] min-content",
          "> li": {
            height: TOP_NAV_HEIGHT_DESKTOP,
          },
          ".topnav-logo": {
            gridRow: "top",
            gridColumn: "logo",
          },
          ".topnav-left": {
            gridRow: "top",
            gridColumn: "left",
          },
          ".topnav-right": {
            gridRow: "top",
            gridColumn: "right",
          },
          ".topnav-burger": {
            gridRow: "top",
            gridColumn: "right",
            display: "none",
          },
          [theme.breakpoints.down(TOP_NAV_WIDTH_MOBILE_MAX)]: {
            gridTemplateColumns: `[logo] ${TOP_NAV_LOGO_WIDTH_MOBILE + 20}px [left] auto [right] 50px`,
            gridTemplateRows: "[top] min-content [menu] auto",
            "> li": {
              height: TOP_NAV_HEIGHT_MOBILE,
            },
            ".topnav-right": {
              gridRow: "menu",
              gridColumn: "logo / 4",
              display: isMobileMenuOpen ? "block" : "none",
              height: "auto",
            },
            ".topnav-burger": {
              display: "flex",
            },
          },
        },
      })}
    >
      {children}
    </Box>
  );
}

function LI_HorseZoneLogo({ isMobileMenuOpen }: { isMobileMenuOpen: boolean }) {
  return (
    <Box
      component="li"
      className="topnav-logo"
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        paddingLeft: 2,
        paddingRight: 2,
        img: {
          display: "block",
        },
        [theme.breakpoints.down(TOP_NAV_WIDTH_MOBILE_MAX)]: {
          paddingLeft: 1,
          paddingRight: 1,
          height: TOP_NAV_HEIGHT_MOBILE,
          borderBottom: isMobileMenuOpen ? "1px solid" : "none",
          borderColor: theme.palette.divider,
          borderBottomWidth: 1,
          img: {
            height: "auto",
            width: TOP_NAV_LOGO_WIDTH_MOBILE,
          },
        },
      })}
    >
      <Box
        component={Link}
        href="/"
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "4px",
          [theme.breakpoints.down(TOP_NAV_WIDTH_MOBILE_MAX)]: {
            gap: "2px",
          },
        })}
      >
        <Image
          src="/logos/header-desktop-spaced.jpg"
          alt={META_BRAND_NAME}
          width={TOP_NAV_LOGO_WIDTH_DESKTOP}
          height="29"
          priority
          unoptimized
        />
        <Typography
          variant="caption"
          sx={(theme) => ({
            "&&": {
              color: "grey.700",
              fontSize: "15px",
              lineHeight: "15px",
              fontWeight: "100 !important",
              textTransform: "uppercase",
              [theme.breakpoints.down(TOP_NAV_WIDTH_MOBILE_MAX)]: {
                fontSize: "12px",
                lineHeight: "12px",
              },
            },
          })}
        >
          {META_AUSTRALIAS_NUMBER_1}
        </Typography>
      </Box>
    </Box>
  );
}

function LI_Link({
  href,
  children,
  onClick,
  onMouseEnter,
  pushRight,
  compact,
  blockLinkClick,
  isSelected,
  isUnderlined,
  hideOnMobile,
}: {
  href: string;
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
  pushRight?: boolean;
  compact?: boolean;
  blockLinkClick?: boolean;
  isSelected?: boolean;
  isUnderlined?: boolean;
  hideOnMobile?: boolean;
}) {
  return (
    <Typography
      component="li"
      sx={(theme) => ({
        position: "relative",
        maxWidth: "100%",
        marginLeft: pushRight ? "auto" : "initial",
        [theme.breakpoints.down(TOP_NAV_WIDTH_MOBILE_MAX)]: {
          marginLeft: "initial",
        },
        [theme.breakpoints.down("sm")]: {
          display: hideOnMobile ? "none" : "block",
        },
        a: {
          maxWidth: "100%",
          backgroundColor: isSelected
            ? NAV_SELECTED_BACKGROUND_COLOR
            : "transparent",
          "&:hover": {
            backgroundColor: !compact
              ? NAV_SELECTED_BACKGROUND_COLOR
              : "transparent",
          },
          height: "100%",
          width: "100%",
          display: "flex",
          alignItems: "center",
          paddingTop: MOBILE_SPACING,
          paddingBottom: MOBILE_SPACING,
          paddingLeft: compact ? 0 : DESKTOP_SPACING,
          paddingRight: compact ? 0 : DESKTOP_SPACING,
          [theme.breakpoints.down(TOP_NAV_WIDTH_MOBILE_MAX)]: {
            paddingLeft: compact ? 0 : MOBILE_SPACING,
            paddingRight: compact ? 0 : MOBILE_SPACING,
            paddingTop: 1,
            paddingBottom: 1,
          },
        },
      })}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
    >
      <a
        href={href}
        onClick={(event) => {
          if (blockLinkClick) {
            event.preventDefault();
            event.nativeEvent.stopImmediatePropagation();
          }
        }}
      >
        {children}
        {isUnderlined && (
          <Box
            sx={(theme) => ({
              backgroundColor: "black",
              position: "absolute",
              left: theme.spacing(DESKTOP_SPACING),
              right: theme.spacing(DESKTOP_SPACING),
              bottom: theme.spacing(DESKTOP_SPACING / 2),
              height: "2px",
              [theme.breakpoints.down(TOP_NAV_WIDTH_MOBILE_MAX)]: {
                left: theme.spacing(MOBILE_SPACING),
                right: theme.spacing(MOBILE_SPACING),
                bottom: theme.spacing(MOBILE_SPACING / 4),
              },
            })}
          />
        )}
      </a>
    </Typography>
  );
}

function UL({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="ul"
      sx={{
        display: "flex",
        width: "100%",
        position: "relative",
      }}
    >
      {children}
    </Box>
  );
}

function LI_Verticals({ children }: { children: React.ReactNode }) {
  const pathName = usePathname();
  const isDetailsPage = pathName.startsWith("/for-sale/details/");
  const isSearchPage = !isDetailsPage && pathName.startsWith("/for-sale");

  return (
    <Box
      component="li"
      className="topnav-left"
      sx={(theme) => ({
        display: "flex",
        overflow: "auto",
        ...HIDE_SCROLLBAR_STYLE,
        [theme.breakpoints.down("sm")]: {
          display: isDetailsPage || !isSearchPage ? "flex" : "none",
        },
      })}
    >
      {children}
    </Box>
  );
}

function LI_Membership({ children }: { children: React.ReactNode }) {
  const SCROLL_INDICATOR_WIDTH = "50px";
  return (
    <Box
      component="li"
      className="topnav-right"
      sx={(theme) => ({
        display: "flex",
        background: "white",
        overflow: "hidden",
        top: 0,
        bottom: 0,
        right: 0,
        "&:before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: "-" + SCROLL_INDICATOR_WIDTH,
          bottom: 0,
          width: SCROLL_INDICATOR_WIDTH,
          background: `linear-gradient(-90deg,
            rgba(255, 255, 255, 1) 0%,
            rgba(255, 255, 255, 0.3) 50%,
            rgba(255, 255, 255, 0.25) 65%,
            rgba(255, 255, 255, 0.075) 75.5%,
            rgba(255, 255, 255, 0.037) 82.85%,
            rgba(255, 255, 255, 0.019) 88%,
            rgba(255, 255, 255, 0) 100%
          )`,
        },
        [theme.breakpoints.down(TOP_NAV_WIDTH_MOBILE_MAX)]: {
          borderTop: "1px solid",
          borderColor: theme.palette.divider,
          position: "initial",
        },
      })}
    >
      <Divider
        orientation="vertical"
        sx={(theme) => ({
          [theme.breakpoints.down(TOP_NAV_WIDTH_MOBILE_MAX)]: {
            display: "none",
          },
        })}
      />
      {children}
    </Box>
  );
}

function MenuList({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="ul"
      sx={{
        listStyle: "none",
        display: "flex",
        columnGap: 5,
        flexDirection: "column",
        "a:hover": {
          textDecoration: "underline",
        },
        maxHeight: "500px",
        flexWrap: "wrap",
      }}
    >
      {children}
    </Box>
  );
}

function MenuDivider({ label }: { label: string }) {
  return (
    <Divider
      sx={{
        marginTop: 2,
        "&:first-child": {
          margin: 0,
        },
      }}
    >
      <Typography variant="caption">{label}</Typography>
    </Divider>
  );
}

function MenuItem({
  href = "",
  label,
  isSelected,
  isLarge,
  onClick,
}: {
  href?: string;
  label: string;
  isSelected?: boolean;
  isLarge?: boolean;
  blockClick?: boolean;
  onClick?: () => void;
}) {
  return (
    <Typography
      component="li"
      sx={(theme) => ({
        fontWeight: isSelected ? "bold" : "initial",
        a: {
          paddingTop: isLarge ? 1 : 0.5,
          paddingBottom: isLarge ? 1 : 0.5,
          display: "block",
          fontSize: isLarge ? theme.typography.h6.fontSize : "inherit",
        },
      })}
    >
      <Link
        href={href}
        onClick={(e) => {
          if (onClick) {
            e.preventDefault();
            e.nativeEvent.stopImmediatePropagation();
            onClick();
          }
        }}
      >
        {label}
      </Link>
    </Typography>
  );
}

function LI_BuyMenu() {
  const pathName = usePathname();
  const isDetailsPage = pathName.startsWith("/for-sale/details/");
  const isSearchPage = !isDetailsPage && pathName.startsWith("/for-sale");
  return (
    <LI_Link
      href={"/for-sale/everything-equine"}
      blockLinkClick={!isDetailsPage}
      isSelected={isSearchPage || isDetailsPage}
      isUnderlined
    >
      <BuyMenu />
    </LI_Link>
  );
}

function LI_SellMenu() {
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<null | HTMLElement>();
  const { setBackdropOpen } = usePageBackdrop();

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPopoverAnchorEl(event.currentTarget);
    setBackdropOpen(true);
  };

  const handlePopoverClose = () => {
    setPopoverAnchorEl(null);
    setBackdropOpen(false);
  };

  const isSellMenuOpen = Boolean(popoverAnchorEl);

  return (
    <>
      <LI_Link
        href={"/sell"}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (popoverAnchorEl) {
            handlePopoverClose();
          } else {
            handlePopoverOpen(event);
          }
        }}
        blockLinkClick={true}
        isUnderlined
        hideOnMobile
      >
        <Typography
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Sell
        </Typography>
        <ExpandMoreIcon />
      </LI_Link>
      <Popover
        id="mouse-over-popover"
        open={isSellMenuOpen}
        anchorEl={popoverAnchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
        sx={{
          cursor: "pointer",
        }}
      >
        <Box
          sx={{
            padding: 3,
            display: "flex",
            gap: 5,
          }}
          // AJB: is the mouse leave quirky?
          onMouseLeave={handlePopoverClose}
          onClick={handlePopoverClose}
        >
          <MenuList>
            <MenuItem href={`/sell`} label="Create ad" />
            <MenuItem href={`/sell/dashboard`} label="Manage ad" />
          </MenuList>
        </Box>
      </Popover>
    </>
  );
}

function LI_MemberMenu({
  setIsMobileMenuOpen,
}: {
  setIsMobileMenuOpen: (value: boolean) => void;
}) {
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<null | HTMLElement>();
  const { session, isAuthenticated } = useLazySession();
  const { setBackdropOpen } = usePageBackdrop();
  const pathName = usePathname();

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPopoverAnchorEl(event.currentTarget);
    setBackdropOpen(true);
  };

  const handlePopoverClose = () => {
    setPopoverAnchorEl(null);
    setBackdropOpen(false);
  };

  const isMenuOpen = Boolean(popoverAnchorEl);

  return (
    <LI_Link
      href={AppendReturnUrl(`/auth`, pathName)}
      blockLinkClick={isAuthenticated}
      onClick={(e) => {
        if (isAuthenticated && !isMenuOpen) {
          handlePopoverOpen(e);
        }
        setIsMobileMenuOpen(false);
      }}
      compact
    >
      {isAuthenticated ? (
        <Box
          sx={{
            display: "flex",
            gap: 1,
          }}
        >
          <PersonIcon />
          <Typography
            sx={(theme) => ({
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              [theme.breakpoints.down("lg")]: {
                maxWidth: "100px",
              },
            })}
          >
            {session?.user?.name || session?.user?.email || "Member"}
          </Typography>
          <ExpandMoreIcon />
        </Box>
      ) : (
        <>Sign Up / Log in</>
      )}
      <Popover
        id="membership-mouse-over-popover"
        open={isMenuOpen}
        anchorEl={popoverAnchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
        sx={{
          cursor: "pointer",
        }}
      >
        <Box
          sx={{
            padding: 3,
            display: "flex",
            gap: 5,
          }}
          // AJB: is the mouse leave quirky?
          onMouseLeave={handlePopoverClose}
          onClick={handlePopoverClose}
        >
          <MenuList>
            <MenuDivider label="Buy" />
            <MenuItem href="/members/watchlist" label="Watch List" />
            <MenuDivider label="Sell" />
            <MenuItem href="/sell" label="Create ad" />
            <MenuItem href="/sell/dashboard" label="Manage ads" />
            <MenuDivider label="User" />
            <MenuItem href="/members/password" label="Update Password" />
            <MenuItem href="/members/profile" label="Profile" />
            <MenuItem label="Logout" onClick={() => signOut()} />
          </MenuList>
        </Box>
      </Popover>
    </LI_Link>
  );
}

export default function GlobalNav() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // AJB: clean up usages
  const pathName = usePathname();
  return (
    <header>
      <NAV isMobileMenuOpen={isMobileMenuOpen}>
        <UL>
          <LI_HorseZoneLogo isMobileMenuOpen={isMobileMenuOpen} />

          <LI_Verticals>
            <UL>
              <LI_BuyMenu />
              <LI_SellMenu />
            </UL>
          </LI_Verticals>

          <LI_Membership>
            <Box
              component="ul"
              sx={(theme) => ({
                display: "flex",
                width: "100%",
                gap: 4,
                paddingLeft: 4,
                paddingRight: 4,
                [theme.breakpoints.down(TOP_NAV_WIDTH_MOBILE_MAX)]: {
                  paddingLeft: 1,
                  paddingRight: 1,
                  paddingTop: 2,
                  paddingBottom: 2,
                  gap: 0,
                  justifyContent: "space-between",
                  height: isMobileMenuOpen ? "auto" : 0,
                  flexDirection: "column",
                  alignItems: "end",
                },
              })}
            >
              <LI_Link
                href="/insurance"
                onClick={() => setIsMobileMenuOpen(false)}
                pushRight
                compact
              >
                Insurance
              </LI_Link>
              <LI_Link
                href="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                pushRight
                compact
              >
                Contact Us
              </LI_Link>
              <LI_Link
                href="/members/watchlist"
                onClick={() => setIsMobileMenuOpen(false)}
                pushRight
                compact
              >
                Watch List
              </LI_Link>
              <LI_MemberMenu setIsMobileMenuOpen={setIsMobileMenuOpen} />

              {!pathName.startsWith("/sell") && (
                <LI_Link
                  href="/sell"
                  onClick={() => setIsMobileMenuOpen(false)}
                  compact
                >
                  <PillButton
                    primary
                    sx={{
                      backgroundColor: BRAND_COLOR,
                    }}
                  >
                    Start selling
                  </PillButton>
                </LI_Link>
              )}
            </Box>
          </LI_Membership>

          <Box
            className="topnav-burger"
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              padding: theme.spacing(1, 2),
            })}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </Box>
        </UL>
      </NAV>
      <Backdrop
        sx={{
          zIndex: TOP_NAV_Z_INDEX - 1,
        }}
        open={isMobileMenuOpen}
        onClick={() => setIsMobileMenuOpen(false)}
      />
    </header>
  );
}
